import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Building2, Utensils, Store, Scissors, CheckCircle2, MapPin, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BRAND, IMAGES } from "@/lib/data";

const SPACES = [
  {
    icon: Building2,
    title: "Office & Co-working",
    body: "Brand-forward offices from 800 to 30,000 sq ft — reception, cabins, meeting rooms, breakout zones and phone booths. Corporate-grade acoustics, cable management and biometric-ready reception.",
  },
  {
    icon: Utensils,
    title: "Restaurants, Cafés & Bars",
    body: "Hospitality interiors that turn footfall into repeat visits — statement bar counters, layered dine-in seating, kitchen coordination and mood-first lighting design.",
  },
  {
    icon: Store,
    title: "Retail & Showrooms",
    body: "Retail interiors that convert — sightlines, focal displays, brand storytelling walls, LED-lit shelving and turnkey shop fit-outs delivered ready-to-open.",
  },
  {
    icon: Scissors,
    title: "Salons, Clinics & Boutiques",
    body: "Calm, premium environments for salons, wellness studios and clinics — hygienic finishes, mirror stations, treatment cabins and reception zones tuned to your brand.",
  },
];

const HIGHLIGHTS = [
  "Turnkey delivery — one team owns design, execution and handover",
  "AI 2D + 360° walkthroughs before a single tile is bought",
  "Vendor-managed civil, MEP, joinery, false ceiling & finishing",
  "Fixed timelines with weekly project reports",
  "GST-compliant billing, project agreement & warranty",
];

const STEPS = [
  { n: "01", t: "Discovery Call", b: "We understand your brand, footfall, ops flow and non-negotiables." },
  { n: "02", t: "AI + Concept", b: "2D and 360° concepts in days, not weeks. Iterate live in our studio." },
  { n: "03", t: "BOQ & Timeline", b: "Itemised commercial BOQ, milestones and vendor plan you can sign off." },
  { n: "04", t: "Execute & Handover", b: "Turnkey execution with a single project manager and clean handover." },
];

export default function Commercial() {
  return (
    <main data-testid="commercial-landing" className="relative z-10">
      <Seo
        title="Commercial Interior Design Kolkata — Office, Café, Retail | ZEBO"
        description="Turnkey commercial interior design in Kolkata — offices, restaurants, cafés, retail shops, showrooms and salons. AI 2D + 360° concepts, fixed timelines, one accountable team."
        path="/commercial"
      />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end pt-32 pb-16 sm:pb-24 bg-[#143D63] overflow-hidden">
        <img
          src={IMAGES.living2}
          alt="ZEBO commercial interior design Kolkata"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/40 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline text-[#F8C21C] mb-5 flex items-center gap-2"
          >
            <Sparkles size={14} /> Turnkey Commercial Interiors — Kolkata
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.05 }}
            className="font-serif text-5xl sm:text-7xl lg:text-[92px] font-light text-[#F9F8F6] tracking-tight leading-[0.92] max-w-5xl"
          >
            Interiors that <em className="text-[#F8C21C] not-italic">earn</em> their square footage.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="text-[#F9F8F6]/80 mt-8 max-w-2xl text-base sm:text-lg leading-relaxed"
          >
            Offices, restaurants, cafés, retail and salons — designed and delivered turnkey by
            {" "}{BRAND.full}. See your future space in AI-generated 2D and 360° before we lift a hammer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-wrap gap-3 mt-10"
          >
            <Link
              to="/consultation?intent=commercial"
              data-testid="commercial-hero-cta"
              className="group inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6] transition-colors duration-300"
            >
              Book a Site Visit
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/ai-studio"
              data-testid="commercial-hero-ai"
              className="inline-flex items-center gap-2 border border-[#F9F8F6]/40 text-[#F9F8F6] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6]/10 transition-colors duration-300"
            >
              <Sparkles size={16} className="text-[#F8C21C]" /> Try AI Commercial Studio
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What we design */}
      <section data-testid="commercial-spaces" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4">What we design</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1] max-w-3xl">
            Every commercial category, one accountable studio.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-[#143D63]/15 border border-[#143D63]/15 mt-14">
          {SPACES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06} className="bg-[#F9F8F6]">
              <div className="p-8 sm:p-10 h-full">
                <s.icon className="text-[#8B0000] mb-5" size={30} />
                <h3 className="font-serif text-3xl text-[#143D63] mb-3 leading-tight">{s.title}</h3>
                <p className="text-[#143D63]/70 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why ZEBO for commercial */}
      <section data-testid="commercial-highlights" className="bg-[#143D63] py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#F8C21C] mb-4">Why ZEBO for commercial</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-[#F9F8F6] leading-[1.05]">
              Purpose-built for spaces that need to open on time.
            </h2>
            <p className="text-[#F9F8F6]/70 mt-6 leading-relaxed">
              A commercial interior isn&apos;t a home — it has an opening date, a P&amp;L and a brand behind it. We plan around all three.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            <ul className="space-y-5">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-3 text-[#F9F8F6]/85 leading-relaxed">
                  <CheckCircle2 className="text-[#F8C21C] mt-1 shrink-0" size={20} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section data-testid="commercial-process" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4">Process</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1] max-w-3xl">
            Four steps, zero surprises.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#143D63]/15 border border-[#143D63]/15 mt-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06} className="bg-[#F9F8F6]">
              <div className="p-8 h-full">
                <p className="overline text-[#F8C21C]">{s.n}</p>
                <h3 className="font-serif text-2xl text-[#143D63] mt-3 mb-3">{s.t}</h3>
                <p className="text-sm text-[#143D63]/70 leading-relaxed">{s.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Local SEO block */}
      <section data-testid="commercial-local-seo" className="bg-[#F9F8F6] py-20 border-t border-[#143D63]/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#143D63] font-light max-w-3xl leading-tight">
              Looking for <span className="text-[#8B0000]">office interior designers in Kolkata</span>? A{" "}
              <span className="text-[#8B0000]">restaurant interior design company</span>? A{" "}
              <span className="text-[#8B0000]">retail shop fit-out partner</span>? That&apos;s ZEBO.
            </h2>
            <p className="text-[#143D63]/70 mt-6 max-w-3xl leading-relaxed">
              We deliver commercial interior design in Kolkata across Rajdanga, Kasba, Salt Lake, New Town, Ballygunge, Park Street, Camac Street, Sector V and beyond — for offices, restaurants, cafés, cloud kitchens, retail stores, salons, clinics, boutiques and showrooms.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="commercial-cta" className="bg-[#143D63] py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-5xl sm:text-6xl font-light text-[#F9F8F6] leading-[1] tracking-tight">
              Ready to open? Let&apos;s design a space that opens with a queue.
            </h2>
            <p className="text-[#F9F8F6]/70 mt-6 max-w-xl leading-relaxed">
              Book a free 30-minute discovery call. Share your space, brand and target opening date — we&apos;ll come back with a phased plan and an initial AI concept.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to="/consultation?intent=commercial"
                data-testid="commercial-final-cta"
                className="group inline-flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6] transition-colors duration-300"
              >
                Book Free Consultation
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="commercial-call-cta"
                className="inline-flex items-center gap-2 border border-[#F9F8F6]/40 text-[#F9F8F6] px-8 py-4 font-bold text-sm hover:bg-[#F9F8F6]/10 transition-colors duration-300"
              >
                <Phone size={16} className="text-[#F8C21C]" /> Call {BRAND.phone}
              </a>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
            <div className="border border-[#F9F8F6]/15 p-8">
              <p className="overline text-[#F8C21C] mb-4">Visit the studio</p>
              <p className="text-[#F9F8F6]/85 leading-relaxed text-sm flex items-start gap-2">
                <MapPin size={16} className="mt-1 shrink-0 text-[#F8C21C]" /> {BRAND.address}
              </p>
              <p className="text-[#F9F8F6]/60 text-xs mt-4">Mon–Sat · 10:00–19:00</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
